import { useId } from 'quasar'
import { $fetch } from '@/boot/fetch'
import { $logger } from '@/boot/logger'
import { $proxy } from '@/boot/proxy'
import { $rollbar } from '@/boot/rollbar'
import { updateProfile } from 'firebase/auth'
import { MINUTE_MS, SECOND_MS } from '@/utils/time.util'
import { Geolocation } from '@capacitor/geolocation'
import { ifStoreIsActive } from '@/utils/store.util'

/**
 * @module SessionStore
 * @category Pinia
 * @description Manages the user session state including authentication, location services, and shopping preferences.
 *
 * This store handles:
 * - User authentication state and token management
 * - Geographic location tracking and permissions
 * - Shopping zip code preferences
 * - Partner experience customization
 * - Session initialization and status
 *
 * It provides reactive state management for session-related data and methods for
 * authentication flow, location services, and session configuration.
 *
 * @requires CartStore
 * @requires UserStore
 */

const SESSION_TIMEOUT = 10 * SECOND_MS
const OMAHA_ZIP = '68144'

export const useSessionStore = defineStore('session', () => {
  const $q = useQuasar()
  const featureFlagStore = useFeatureFlagStore()
  const { featureFlags } = featureFlagStore
  const { flagsAreInitialized } = storeToRefs(featureFlagStore)

  let authRefreshInterval = null
  const sessionKey = useId()
  const sessionAuthToken = ref(null)
  const sessionAuthType = ref(null)
  const sessionFirebaseUser = ref(null)
  const location = ref({ accuracy: null, latitude: null, longitude: null })
  const locationLoadingText = ref('Locating')
  const hasGrantedLocation = ref(false)

  // Auth-ready gate: resolves once Firebase has fired its first onAuthStateChanged callback.
  // awaitSessionInit() waits on this before starting the SESSION_TIMEOUT countdown so the
  // timeout only measures our own init work, not Firebase's auth resolution time.
  let _resolveAuthReady
  const _authReadyPromise = new Promise(resolve => {
    _resolveAuthReady = resolve
  })

  /**
   * Signal that Firebase auth state has been checked (signed-in or signed-out).
   * Must be called at the top of every onAuthStateChanged / authStateChange handler.
   * @memberOf module:SessionStore
   */
  function markAuthChecked() {
    _resolveAuthReady()
  }

  const shoppingZip = ref($q.localStorage.getItem('peg_geo')?.zip || OMAHA_ZIP)
  // update key data on shopping zip change
  watch(shoppingZip, (curr, prev) => {
    if (curr !== prev) {
      const geo = $q.localStorage.getItem('peg_geo')
      // persist in session storage
      $q.localStorage.setItem('peg_geo', { ...geo, zip: curr })
      // TODO: disabled to determine whether this is needed | dsolano 2026-02-10
      // It causes issues and we should be handling updates more granularly, like in useQuery keys that depend on shopping zip
      // force app to re-render
      // refreshSessionKey()
    }
  })

  /* ----- SESSION STATUS ----- */
  const sessionIsInitialized = ref(false)
  const sessionIsInitializing = ref(false)
  watch(sessionIsInitialized, curr => {
    if (curr) {
      $q.loading.hide()
    }
  })
  const sessionIsReady = computed(() => sessionIsInitialized.value && flagsAreInitialized.value)
  const sessionIsAuthed = computed(() => Boolean(sessionAuthToken.value))
  const sessionIsPhoneAuth = computed(() => sessionIsAuthed.value && sessionAuthType.value === 'phone')
  const sessionIsEmailAuth = computed(() => sessionIsAuthed.value && sessionAuthType.value === 'email')
  const sessionPhoneAuthIsComplete = computed(() =>
    Boolean(sessionIsPhoneAuth.value && useUserStore().userHasVerifiedPhone && useUserStore().userHasVerifiedEmail)
  )
  const sessionUserIsEstablished = computed(() => Boolean(sessionIsAuthed.value && useUserStore().userDataExists))
  const sessionIsPreviewMode = computed(() =>
    Boolean(!$q.platform.is.nativeMobile && featureFlags.previewMode?.enabled && !sessionIsAuthed.value)
  )
  const sessionHasError = ref(false)
  const apiUnavailable = ref(false)
  const sessionPartnerExpSlug = ref($q.localStorage.getItem('peg_partner_exp') || null)

  /**
   * Persists the partner experience slug in local storage.
   * @memberOf module:SessionStore
   * @param {string} slug - The partner experience slug to be stored.
   */
  function setPartnerExpSlug(slug) {
    $q.localStorage.setItem('peg_partner_exp', slug)
    sessionPartnerExpSlug.value = slug
    // clear legacy key
    $q.localStorage.removeItem('peg_brand_exp')
  }

  /**
   * Clears the partner experience slug from local storage.
   * @memberOf module:SessionStore
   */
  function clearPartnerExpSlug() {
    $q.localStorage.removeItem('peg_partner_exp')
    sessionPartnerExpSlug.value = null
  }

  const hasValidLocation = computed(() => location.value.latitude !== null && location.value.longitude !== null)

  watchEffect(() => {
    // handle session error state redirect
    if (sessionHasError.value && !window.location.pathname.includes('/error')) {
      window.location.href = '/error'
    }
  })

  /* ----- ACTIONS ----- */
  /**
   * Refresh the session key - forces main page content to be re-rendered
   * @memberOf module:SessionStore
   * @returns {void}
   */
  function refreshSessionKey() {
    // capture scroll position before refresh so we can restore it
    const scrollY = window.scrollY

    sessionKey.value = useId().value

    // TODO: not sure why we were doing this - remove once we confirm it's not needed | dsolano 2026-02-10
    // re-fetch user data if user is authenticated
    // if (sessionIsAuthed.value) {
    //   useUserStore().getUser()
    // }

    // restore scroll position
    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'smooth' })
    }, 500)
  }

  /**
   * Set the session error state
   */
  function setErrorState(hasError) {
    sessionHasError.value = hasError
  }

  /**
   * Set the auth token for the user
   * @memberOf module:SessionStore
   * @param {Object} firebaseUser - Firebase user object
   */
  async function establishAuth(firebaseUser, { forceRefresh = false } = {}) {
    if (authRefreshInterval) {
      clearInterval(authRefreshInterval)
      authRefreshInterval = null
    }

    if (firebaseUser) {
      sessionFirebaseUser.value = firebaseUser
      sessionAuthType.value = firebaseUser.phoneNumber && !firebaseUser.email ? 'phone' : 'email'
      sessionAuthToken.value = await firebaseUser.getIdToken(forceRefresh)
      // $logger.debug('AUTH TOKEN', sessionAuthToken.value)

      // refresh token every 30 minutes (force refresh on scheduled intervals)
      authRefreshInterval = setInterval(refreshAuth, MINUTE_MS * 30)
    } else {
      sessionFirebaseUser.value = null
      sessionAuthToken.value = null
      sessionAuthType.value = null
    }
  }

  async function refreshAuth() {
    if (!sessionFirebaseUser.value) return false
    try {
      await establishAuth(sessionFirebaseUser.value, { forceRefresh: true })
      return true
    } catch (e) {
      $rollbar.error('Token refresh failed', { error: e?.message })
      return false
    }
  }

  // Proactively refresh token when tab becomes visible (browser throttles
  // setInterval in background tabs, so the 30-min refresh cycle may stall)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && sessionFirebaseUser.value) {
        refreshAuth()
      }
    })
  }

  async function destroyAuth() {
    return await establishAuth(null)
  }

  /**
   * Sign user in and fetch user data from PEG API
   * @memberOf module:SessionStore
   * @async
   * @param {Object} firebaseUser - Firebase user object
   */
  async function init(firebaseUser) {
    if (featureFlags.maintenanceMode?.enabled) {
      sessionIsInitialized.value = true
      return { abort: true }
    }

    // Multiple initialization should NEVER HAPPEN
    // If it does, there's a bug somewhere that needs to be fixed
    if (sessionIsInitializing.value) {
      $rollbar.error('Duplicate session init attempted')
      return { abort: true }
    }

    sessionIsInitializing.value = true
    sessionIsInitialized.value = false

    const { user, init: initUserStore } = useUserStore()

    try {
      await establishAuth(firebaseUser || sessionFirebaseUser.value)

      // initialize the user store
      const { success } = await initUserStore()

      if (!success) {
        throw new Error('Failed to initialize user data')
      }

      const sessionGeo = $q.localStorage.getItem('peg_geo')
      const sessionZip = sessionGeo?.zip || user?.shoppingZip
      if (sessionZip) {
        // TODO: retain user's shopping zip if already set?
        shoppingZip.value = sessionZip
      } else {
        await setShoppingZipByIp()
      }

      // update display name if changed
      if (user?.firstName && user?.lastName) {
        const displayName = `${user.firstName} ${user.lastName}`
        if (firebaseUser?.displayName !== displayName) {
          updateProfile(firebaseUser, { displayName })
        }
      }
    } catch (error) {
      $rollbar.error('Sign in error', error)
      return { user: null, error }
    } finally {
      // Always set this to true, even if there were errors
      sessionIsInitialized.value = true
      sessionIsInitializing.value = false
    }

    return { user }
  }

  /**
   * Destroy the current session (logout)
   * Clears all user-specific data while preserving session preferences
   * @memberOf module:SessionStore
   * @returns {void}
   */
  async function destroy() {
    try {
      // 1. Clear dependent stores first (these depend on user data)
      ifStoreIsActive(useCheckoutStore, s => s.resetState())
      ifStoreIsActive(useCartStore, s => s.resetState())
      ifStoreIsActive(useCustomerReturnStore, s =>
        s.resetState({
          clearConfirmation: true,
          keepRetailer: false,
          keepOrderEmail: false,
        })
      )

      // 2. Clear user-related stores
      ifStoreIsActive(useAccountSetupStore, s => s.resetState())
      ifStoreIsActive(useUserReturnsStore, s => s.resetState())

      // 3. Clear user and payment data
      ifStoreIsActive(usePaymentGatewayStore, s => s.resetState())
      ifStoreIsActive(useUserStore, s => s.resetState())

      // 4. Clear Firebase auth
      await destroyAuth()

      // Note: Session preferences (shoppingZip, partnerExp) are preserved
    } catch (error) {
      $rollbar.error('Sign out error', error)
    } finally {
      // Always set this to true, even if there were errors
      sessionIsInitialized.value = true
    }
  }

  /**
   * Wait for auth initialization to complete
   * @memberOf module:SessionStore
   * @returns {Promise} - Promise that resolves when auth is initialized
   */
  async function awaitSessionInit() {
    if (!sessionIsReady.value) {
      $q.loading.show()

      // Wait for Firebase to signal auth state before starting the timeout.
      // This ensures the SESSION_TIMEOUT only covers our own init work
      // (initUserStore, etc.), not Firebase's auth resolution time.
      await _authReadyPromise

      if (sessionIsReady.value) {
        $q.loading.hide()
        $logger.debug('SESSION IS READY - resolved after auth check')
        return
      }

      return new Promise(resolve => {
        // Add a timeout to prevent infinite waiting
        const timeout = setTimeout(async () => {
          $rollbar.error('Session init timeout')
          // sign user out if initialization fails
          await destroy()

          $q.loading.hide()
          $q.notify({
            type: 'error-long-center',
            message: "We couldn't get your session started. Please try signing in again.",
          })
          unwatch()
          resolve()
        }, SESSION_TIMEOUT)

        // Watch sessionIsReady instead of polling with setInterval
        const unwatch = watch(sessionIsReady, ready => {
          $logger.debug(`${!ready ? '❌ Session NOT' : '✅ Session IS'} Ready`)
          if (ready) {
            $q.loading.hide()
            unwatch()
            clearTimeout(timeout)
            resolve()
          }
        })
      })
    }

    $logger.debug('SESSION IS READY - no await')

    return Promise.resolve()
  }

  /**
   * Set the user's shopping zip code
   * @memberOf module:SessionStore
   * @param {String} zip - Zip code
   * @returns {Promise} - Promise with request status
   */
  async function setShoppingZip(zip) {
    const sessionGeo = $q.localStorage.getItem('peg_geo')
    // watcher above will update the session key and persist the zip in local storage
    shoppingZip.value = zip || sessionGeo?.zip

    if (sessionUserIsEstablished.value) {
      return await useUserStore().updateUser({ shoppingZip: shoppingZip.value.toString() })
    }

    return { success: true }
  }

  /**
   * Set the user's shopping zip code based on IP address
   * @memberOf module:SessionStore
   * @returns {Promise} - Promise with request status
   */
  async function setShoppingZipByIp() {
    // Drey updated this to always use the proxy instead of local fetch to align with native app
    // TODO: Should we keep this or revert to local fetch?
    // if ($q.platform.is.nativeMobile) {
    // this points the request to the legacy rrc-proxy endpoint on native mobile
    const resp = await $proxy.fetch('/geolocation')
    const geoData = resp?.data || {}
    // } else {
    //   geoData = await $fetch.fetch('/v1/geolocation')
    // }

    const { postalCode: zip, country, subdivision, success, ...rest } = geoData || {}

    if (success) {
      $q.localStorage.setItem('peg_geo', { ...rest, zip, country: country?.code, state: subdivision?.code })
    }

    // fallback to default zip. setShoppingZip chains into updateUser(), which
    // now throws on API error; this call is fire-and-forget, so swallow + log.
    setShoppingZip(zip || OMAHA_ZIP).catch(error => {
      $logger.error('Failed to set shopping zip from IP', error)
    })
  }

  /**
   * Check if the user has granted location permission and set related flag
   * @memberOf module:SessionStore
   * @returns {void}
   */
  async function checkLocationPermission() {
    const { location } = await Geolocation.checkPermissions()
    hasGrantedLocation.value = location === 'granted'
  }

  /**
   * Get the user's geolocation
   * @memberOf module:SessionStore
   * @returns {Object} - Geolocation data
   */
  async function getLocation() {
    setTimeout(() => {
      locationLoadingText.value = 'Honing In'

      setTimeout(() => {
        locationLoadingText.value = 'Verifying'
      }, 3000)
    }, 6000)
    const newPosition = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    })

    const newLocation = {
      latitude: newPosition.coords.latitude,
      longitude: newPosition.coords.longitude,
      accuracy: newPosition.coords.accuracy,
    }
    location.value = { ...newLocation }

    // re-check permission to set flag
    await checkLocationPermission()

    return newPosition
  }

  /**
   * Check if the PEG API is reachable and update apiUnavailable state
   * @memberOf module:SessionStore
   * @returns {Promise<void>}
   */
  async function checkApiConnection() {
    apiUnavailable.value = !(await $fetch.checkApiConnection())
  }

  /**
   * Reset the user's location data
   * @memberOf module:SessionStore
   * @returns {void}
   */
  function resetLocation() {
    location.value = { accuracy: null, latitude: null, longitude: null }
  }

  return {
    // state
    sessionKey,
    sessionIsInitialized,
    sessionIsReady,
    sessionIsAuthed,
    sessionIsPhoneAuth,
    sessionIsEmailAuth,
    sessionAuthToken,
    // Exported so Cypress e2e specs can $state.sessionAuthType = 'phone'
    // to drive the sessionIsPhoneAuth / sessionPhoneAuthIsComplete
    // computeds — no production caller reads this directly (callers
    // read the computed wrappers above).
    sessionAuthType,
    sessionUserIsEstablished,
    sessionPhoneAuthIsComplete,
    sessionHasError,
    apiUnavailable,
    sessionPartnerExpSlug,
    shoppingZip,
    location,
    locationLoadingText,
    hasGrantedLocation,
    // computed
    hasValidLocation,
    sessionIsPreviewMode,
    // actions
    awaitSessionInit,
    checkLocationPermission,
    getLocation,
    init,
    destroy,
    refreshSessionKey,
    resetLocation,
    setShoppingZip,
    clearPartnerExpSlug,
    setPartnerExpSlug,
    setErrorState,
    checkApiConnection,
    markAuthChecked,
    refreshAuth,
  }
})
